<template>
  <div class="w-full flex flex-col gap-5 px-5 pt-5 pb-7">
    <!-- Date Range Selector -->
    <div class="flex items-center">
      <div
        v-on-clickaway="() => { showDateRangeSelector = false }"
        class="relative"
      >
        <button
          class="flex items-center gap-0.5 p-1 rounded-md"
          :class="showDateRangeSelector ? 'bg-neutral-25' : 'bg-transparent hover:bg-neutral-25'"
          @click="showDateRangeSelector = !showDateRangeSelector"
        >
          <CalendarIcon class="text-icon-normal" />
          <BaseText
            type="label"
            size="sm"
            class="text-text-muted px-1"
          >
            {{ dateFilterLabel }}
          </BaseText>
        </button>
        <div
          v-if="showDateRangeSelector"
          class="date-selector-dropdown"
        >
          <DateRangeSelectorDropdown
            :selected-date-range.sync="selectedDateRange"
            :date-filter-label.sync="dateFilterLabel"
            :excluded-presets="['allTime']"
            close-on-apply
            @close="showDateRangeSelector = false"
          />
        </div>
      </div>
    </div>

    <!-- Savings Cards -->
    <div class="grid grid-cols-4 gap-3">
      <div
        v-for="card in savingsCards"
        :key="card.key"
        class="flex flex-col justify-between gap-3 p-4 rounded-xl border border-border-normal"
        style="min-height: 148px;"
      >
        <div class="flex flex-col gap-3">
          <div class="flex items-start gap-1">
            <BaseText
              type="label"
              size="sm"
              class="flex-1 text-text-normal"
            >
              {{ card.label }}
            </BaseText>
            <BaseTooltipV2
              :text="card.tooltip"
              position="top"
              alignment="center"
            >
              <QuestionMarkCircleIcon class="text-text-subdued flex-shrink-0" />
            </BaseTooltipV2>
          </div>
          <div
            v-if="loadingSavings"
            class="h-8 w-24 rounded-md skeleton"
          />
          <BaseText
            v-else
            type="display"
            size="2xs"
            class="text-text-muted"
          >
            {{ formatCurrency(card.value) }}
          </BaseText>
        </div>

        <template v-if="!loadingSavings">
          <div
            v-if="card.status === 'monitoring'"
            class="flex items-center gap-1"
          >
            <StopwatchIcon class="text-secondary-yellow-200 flex-shrink-0" />
            <BaseText
              type="label"
              size="sm"
              class="text-secondary-yellow-200"
            >
              Monitoring
            </BaseText>
          </div>
          <div
            v-else-if="card.change !== null"
            class="flex items-center gap-1"
          >
            <PeriodDiffIcon
              class="flex-shrink-0"
              :class="card.change >= 0 ? 'text-secondary-green-200' : 'text-secondary-red-200'"
              :style="card.change < 0 ? 'transform: scaleY(-1)' : ''"
            />
            <BaseText
              type="label"
              size="sm"
              :class="card.change >= 0 ? 'text-secondary-green-200' : 'text-secondary-red-200'"
            >
              {{ formatPercent(card.change) }}
            </BaseText>
            <BaseText
              type="body"
              size="sm"
              class="text-text-normal"
            >
              vs. previous period
            </BaseText>
          </div>
        </template>
      </div>
    </div>

    <div class="flex items-start gap-1">
      <MoneyBackIcon class="text-icon-normal flex-shrink-0" />
      <BaseText
        type="body"
        size="sm"
        class="text-text-normal"
      >
        Refunds are paid by Meta directly as ad credits
      </BaseText>
    </div>

    <!-- Connected Integrations -->
    <div class="flex flex-col bg-white border border-border-normal rounded-2xl overflow-hidden">
      <div
        v-for="integration in integrations"
        :key="integration.key"
        class="flex items-start gap-2 p-4 border-b border-border-normal"
      >
        <component
          :is="integration.icon"
          class="text-icon-normal flex-shrink-0"
        />
        <div class="flex-1 min-w-0 flex flex-col gap-1">
          <div class="flex items-center gap-2">
            <BaseText
              type="label"
              size="sm"
              class="text-text-muted"
            >
              {{ integration.name }}
            </BaseText>
            <div
              v-if="integration.status"
              class="px-1.5 py-0.5 rounded-md"
              :class="integration.status.bgClass"
            >
              <BaseText
                type="label"
                size="xs"
                :class="integration.status.textClass"
              >
                {{ integration.status.label }}
              </BaseText>
            </div>
          </div>
          <BaseText
            type="body"
            size="sm"
            class="text-text-normal truncate"
          >
            {{ integration.description }}
          </BaseText>
        </div>
      </div>
      <div
        v-if="loadingIntegrations"
        class="flex items-center gap-2 p-4 border-b border-border-normal"
      >
        <div class="h-5 w-48 rounded-md skeleton" />
      </div>
      <div class="p-4">
        <BaseText
          type="body"
          size="sm"
          class="text-text-normal"
        >
          More platforms are coming soon
        </BaseText>
      </div>
    </div>
  </div>
</template>

<script>
import LensAPI from '@/api/lens'
import { mixin as clickaway } from 'vue-clickaway2'
import { getPresetDateRanges, getTimeAgoShorthand } from '@/utils/dateUtils'
import { getPlatformIcon } from '@/utils/lens/dataSourceUtils'
import DateRangeSelectorDropdown from '../../../search/TopBarComponents/DateRangeSelectorDropdown.vue'

// Icons
import CalendarIcon from '../../../globals/Icons/CalendarIcon.vue'
import QuestionMarkCircleIcon from '../../../globals/Icons/QuestionMarkCircleIcon.vue'
import PeriodDiffIcon from '../../../globals/Icons/LensIcons/PeriodDiffIcon.vue'
import StopwatchIcon from '../../../globals/Icons/StopwatchIcon.vue'
import MoneyBackIcon from '../../../globals/Icons/MoneyBackIcon.vue'
import EmbedIcon from '../../../globals/Icons/EmbedIcon.vue'

const SAVINGS_CARDS = [
  {
    key: 'total',
    label: 'Total Savings',
    tooltip: 'Everything Spendback has saved you in the selected period',
    valueKey: 'total_savings',
    changeKey: 'total_savings_change',
    statusKey: 'total_savings_status'
  },
  {
    key: 'refunds',
    label: 'Savings by Refund Recovery',
    tooltip: 'Ad credits recovered from the platforms for invalid charges',
    valueKey: 'refund_recovery',
    changeKey: 'refund_recovery_change',
    statusKey: 'refund_recovery_status',
    alwaysMonitoring: true
  },
  {
    key: 'removals',
    label: 'Proactive Savings by Removals',
    tooltip: 'Spend avoided by removing invalid traffic before it was billed',
    valueKey: 'proactive_removals',
    changeKey: 'removal_change',
    statusKey: 'removal_status',
    alwaysMonitoring: true
  },
  {
    key: 'invoices',
    label: 'Savings by Invoice Corrections',
    tooltip: 'Credits from billing errors corrected on your invoices',
    valueKey: 'invoice_corrections',
    changeKey: 'invoice_correction_change',
    statusKey: 'invoice_correction_status'
  }
]

const PIXEL_STATUSES = {
  verified: { label: 'Active', bgClass: 'bg-secondary-green-10', textClass: 'text-secondary-green-300' },
  pending: { label: 'Pending', bgClass: 'bg-secondary-yellow-10', textClass: 'text-secondary-yellow-200' },
  pending_activation: { label: 'Pending', bgClass: 'bg-secondary-yellow-10', textClass: 'text-secondary-yellow-200' },
  not_installed: { label: 'Not installed', bgClass: 'bg-secondary-red-10', textClass: 'text-secondary-red-200' },
  inactive: { label: 'Inactive', bgClass: 'bg-secondary-red-10', textClass: 'text-secondary-red-200' }
}

const CONNECTION_STATUSES = {
  connected: { label: 'Connected', bgClass: 'bg-secondary-green-10', textClass: 'text-secondary-green-300' },
  pending_activation: { label: 'Pending', bgClass: 'bg-secondary-yellow-10', textClass: 'text-secondary-yellow-200' },
  needs_reconnect: { label: 'Needs reconnect', bgClass: 'bg-secondary-red-10', textClass: 'text-secondary-red-200' },
  disconnected: { label: 'Disconnected', bgClass: 'bg-neutral-25', textClass: 'text-text-normal' }
}

const normalizeAccountId = id => String(id).replace(/^act_/, '')

// Local calendar day, not UTC, so late-evening ranges don't shift a day
const toDateParam = (date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null
  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

export default {
  name: 'SpendBackOverviewSection',
  components: {
    DateRangeSelectorDropdown,
    CalendarIcon,
    QuestionMarkCircleIcon,
    PeriodDiffIcon,
    StopwatchIcon,
    MoneyBackIcon
  },
  mixins: [clickaway],
  props: {
    lensId: {
      type: String,
      required: true
    },
    // Meta ad accounts on this lens, used to name the connected accounts
    accounts: {
      type: Array,
      default: () => []
    }
  },
  data () {
    const { name, start, end } = getPresetDateRanges().last30Days
    return {
      selectedDateRange: { start, end },
      dateFilterLabel: name,
      showDateRangeSelector: false,

      // Savings for this lens's Spendback property, null until it has one
      savings: null,
      pixel: null,
      connections: [],

      loadingSavings: true,
      loadingIntegrations: true
    }
  },
  computed: {
    savingsCards () {
      const savings = this.savings || {}
      return SAVINGS_CARDS.map(card => {
        const value = toNumberOrNull(savings[card.valueKey]) || 0
        const change = toNumberOrNull(savings[card.changeKey])
        const isMonitoring = card.alwaysMonitoring || savings[card.statusKey] === 'monitoring'
        return { ...card, value, change, status: isMonitoring ? 'monitoring' : null }
      })
    },
    accountNamesById () {
      return this.accounts.reduce((acc, account) => {
        acc[normalizeAccountId(account.ad_account_id)] = account.ad_account_name
        return acc
      }, {})
    },
    integrations () {
      if (this.loadingIntegrations) return []

      const pixelRow = {
        key: 'pixel',
        name: 'Tracking Pixel',
        icon: EmbedIcon,
        status: PIXEL_STATUSES[this.pixel?.status] || null,
        description: this.pixel?.last_event_at
          ? `Last event received ${getTimeAgoShorthand(new Date(this.pixel.last_event_at))} ago`
          : null
      }

      const metaRows = this.connections
        .filter(connection => connection?.platform === 'meta_ads' && connection.account_id)
        .map(connection => {
          const accountId = normalizeAccountId(connection.account_id)
          const accountName = this.accountNamesById[accountId]
          return {
            key: `meta-${accountId}`,
            name: 'Meta Ads',
            icon: getPlatformIcon('meta'),
            status: CONNECTION_STATUSES[connection.status] || null,
            description: accountName ? `${accountName} · act_${accountId}` : `act_${accountId}`
          }
        })

      return [pixelRow, ...metaRows]
    }
  },
  watch: {
    selectedDateRange () {
      this.fetchSavings()
    },
    lensId: {
      immediate: true,
      handler (lensId) {
        if (!lensId) return
        this.fetchSavings()
        this.fetchIntegrations()
      }
    }
  },
  methods: {
    async fetchSavings () {
      const { start, end } = this.selectedDateRange
      if (!start || !end) return

      this.loadingSavings = true
      try {
        const { data } = await LensAPI.Lens.SpendBack.getSavings(this.lensId, {
          startDate: toDateParam(start),
          endDate: toDateParam(end)
        })
        // null means no Spendback property yet, which renders as zeroed cards
        const savings = Array.isArray(data) ? data[0] : data
        this.savings = savings && typeof savings === 'object' ? savings : null
      } catch (error) {
        console.error(error)
        this.$showAlert({
          type: 'error',
          message: error?.response?.data?.message || 'Could not load your savings. Please refresh and try again.'
        })
        this.savings = null
      } finally {
        this.loadingSavings = false
      }
    },
    async fetchIntegrations () {
      this.loadingIntegrations = true
      // Each row degrades on its own so one failing request doesn't blank the list
      const [pixelResult, connectResult] = await Promise.allSettled([
        LensAPI.Lens.SpendBack.getPixel(this.lensId),
        LensAPI.Lens.SpendBack.getConnectStatus(this.lensId, 'meta_ads')
      ])

      if (pixelResult.status === 'fulfilled') {
        // Lens server wraps the Spendback payload, so the pixel sits at data.data
        this.pixel = pixelResult.value?.data?.data || null
      } else {
        console.error(pixelResult.reason)
      }

      if (connectResult.status === 'fulfilled') {
        const data = connectResult.value?.data
        const connections = Array.isArray(data) ? data : data?.data
        this.connections = Array.isArray(connections) ? connections : []
      } else {
        console.error(connectResult.reason)
      }

      this.loadingIntegrations = false
    },
    formatCurrency (value) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: this.savings?.currency || 'USD',
        maximumFractionDigits: 0
      }).format(value)
    },
    formatPercent (value) {
      return `${Math.abs(value).toFixed(1)}%`
    }
  }
}
</script>

<style scoped>
.date-selector-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 500;
}
</style>
